import { useEffect, useState } from 'react';
import { X, Loader2, Calendar as CalendarIcon, RotateCw, Zap } from 'lucide-react';
import { appointmentService } from '../_shared/api';
import type {
  Appointment,
  AppointmentPeriodo,
  Contract,
  SlotsAvailability,
} from '../_shared/types';
import Calendar from './Calendar';
import { useModalKeys } from '../../../hooks/useModalKeys';

/**
 * AppointmentModal — agendar/reagendar instalação de um contrato aprovado
 * (porte do web novo pro painel, Fase 31). Usa a coleção unificada de
 * agendamentos e respeita as vagas configuradas no módulo agendamento;
 * modo encaixe pede aprovação (admin/monitor) antes de ir pro ERP.
 */
interface Props {
  contract: Contract;
  onClose: () => void;
  onScheduled?: () => void;
}

function isoToBR(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  return `${dd}/${mm}/${d.getFullYear()}`;
}

function formatDateBR(d: Date): string {
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  return `${dd}/${mm}/${d.getFullYear()}`;
}

export default function AppointmentModal({ contract, onClose, onScheduled }: Props) {
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [existing, setExisting] = useState<Appointment | null>(null);
  const [availability, setAvailability] = useState<SlotsAvailability | null>(null);
  const [date, setDate] = useState<Date | null>(null);
  const [periodo, setPeriodo] = useState<AppointmentPeriodo | null>(null);
  const [squeezeMode, setSqueezeMode] = useState(false);

  const cidade =
    (contract.data?.cidade as string | undefined)?.toUpperCase() ?? contract.popNome ?? '';

  // Esc fecha só este modal (fica no topo da pilha, sobre o de detalhes)
  useModalKeys(onClose);

  useEffect(() => {
    setLoading(true);
    setDate(null);
    setPeriodo(null);
    setSqueezeMode(false);
    Promise.all([
      appointmentService.getByContract(contract.id).catch(() => null),
      cidade
        ? appointmentService.getAvailability(cidade).catch(() => null)
        : Promise.resolve(null),
    ])
      .then(([appt, avail]) => {
        setExisting(appt);
        setAvailability(avail);
        if (appt) {
          setDate(new Date(appt.data));
          setPeriodo(appt.periodo);
        }
      })
      .finally(() => setLoading(false));
  }, [contract.id, cidade]);

  async function handleSubmit(): Promise<void> {
    if (!date || !periodo) {
      alert('Selecione a data e o período.');
      return;
    }

    setSubmitting(true);
    try {
      const dataBR = formatDateBR(date);
      if (squeezeMode) {
        await appointmentService.create({
          contractId: contract.id,
          data: dataBR,
          periodo,
          isSqueeze: true,
        });
        alert('Solicitação de encaixe enviada para análise.');
      } else if (existing && existing.status !== 'rejected_squeeze') {
        await appointmentService.reschedule(existing.id, { data: dataBR, periodo });
        alert('Instalação reagendada com sucesso!');
      } else {
        await appointmentService.create({ contractId: contract.id, data: dataBR, periodo });
        alert('Instalação agendada com sucesso!');
      }
      onScheduled?.();
      onClose();
    } catch (err) {
      const e = err as { response?: { data?: { message?: string } }; message?: string };
      alert('Erro: ' + (e.response?.data?.message ?? e.message ?? 'desconhecido'));
    } finally {
      setSubmitting(false);
    }
  }

  const cidadeOK = !!cidade;
  const hasActiveAppointment = !!existing && existing.status !== 'rejected_squeeze';
  const headerIcon = squeezeMode ? (
    <Zap size={20} className="text-white" />
  ) : hasActiveAppointment ? (
    <RotateCw size={20} className="text-white" />
  ) : (
    <CalendarIcon size={20} className="text-white" />
  );
  const submitLabel = squeezeMode
    ? 'Solicitar encaixe'
    : hasActiveAppointment
      ? 'Reagendar'
      : 'Agendar';
  const submitColor = squeezeMode
    ? 'bg-warning-solid hover:bg-warning-solid-hover disabled:bg-warning-soft'
    : 'bg-primary-solid hover:bg-primary-solid-hover disabled:bg-primary-soft';

  return (
    <div className="fixed inset-0 z-[60] bg-black/70 flex items-end sm:items-center sm:justify-center sm:p-4">
      <div className="bg-canvas w-full sm:max-w-md sm:rounded-2xl border-t sm:border border-active flex flex-col max-h-[100vh] sm:max-h-[92vh]">
        {/* Header */}
        <div className="px-5 py-4 border-b border-line flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-primary-solid flex items-center justify-center flex-shrink-0">
            {headerIcon}
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="font-bold text-white">
              {existing ? 'Reagendar Instalação' : 'Agendar Instalação'}
            </h2>
            <p className="text-xs text-fg-2 truncate">
              #{contract.erpContratoId} · {contract.clienteNome}
            </p>
          </div>
          <button onClick={onClose} className="text-fg-2 hover:text-white">
            <X size={22} />
          </button>
        </div>

        {/* Conteúdo */}
        <div className="overflow-y-auto flex-1 p-5 space-y-4">
          {/* Resumo */}
          <div className="bg-hover rounded-2xl p-4">
            <Row label="Cliente" value={contract.clienteNome} />
            <Row label="Cidade" value={cidade || '—'} />
            <Row label="Bairro" value={String(contract.data?.bairro ?? '—')} />

            {hasActiveAppointment && existing && (
              <div className="mt-3 bg-primary-soft/30 border border-primary-line/40 rounded-xl px-3 py-2">
                <p className="text-primary text-xs">
                  Atualmente agendado:{' '}
                  <span className="font-bold">
                    {isoToBR(existing.data)} · {existing.periodo}
                  </span>
                </p>
              </div>
            )}
            {existing &&
              existing.status === 'rejected_squeeze' &&
              existing.squeezeRejectionReason && (
                <div className="mt-3 bg-orange-900/30 border border-orange-700/40 rounded-xl px-3 py-2">
                  <p className="text-orange-300 text-xs">
                    <span className="font-bold">Encaixe anterior reprovado:</span>{' '}
                    {existing.squeezeRejectionReason}
                  </p>
                </div>
              )}
          </div>

          {/* Toggle de encaixe */}
          {cidadeOK && (
            <button
              onClick={() => {
                setSqueezeMode((v) => !v);
                setDate(null);
                setPeriodo(null);
              }}
              className={`w-full flex items-center gap-3 rounded-2xl p-3 text-left transition ${
                squeezeMode
                  ? 'bg-warning-soft/30 border border-warning-line/40'
                  : 'bg-hover border border-active'
              }`}
            >
              <Zap size={18} className={squeezeMode ? 'text-warning' : 'text-muted'} />
              <div className="flex-1 min-w-0">
                <p
                  className={`text-sm font-semibold ${squeezeMode ? 'text-warning' : 'text-white'}`}
                >
                  {squeezeMode ? 'Modo encaixe ativo' : 'Solicitar encaixe'}
                </p>
                <p className={`text-[11px] ${squeezeMode ? 'text-warning' : 'text-fg-2'}`}>
                  {squeezeMode
                    ? 'Você pode escolher hoje ou amanhã, mesmo sem vaga. Vai para aprovação.'
                    : 'Liberar hoje/amanhã ou data sem vaga, com aprovação.'}
                </p>
              </div>
            </button>
          )}

          {!cidadeOK && (
            <div className="bg-warning-soft/30 border border-warning-line/40 rounded-2xl p-4">
              <p className="text-warning text-sm">
                Cidade não identificada no contrato — não é possível agendar.
              </p>
            </div>
          )}

          {/* Calendar */}
          {cidadeOK && (
            <div>
              {loading ? (
                <div className="py-10 flex justify-center">
                  <Loader2 size={28} className="animate-spin text-primary" />
                </div>
              ) : (
                <Calendar
                  selectedDate={date}
                  onDateSelect={setDate}
                  selectedPeriodo={periodo}
                  onPeriodoSelect={setPeriodo}
                  availability={availability}
                  squeezeMode={squeezeMode}
                />
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-line px-5 py-3 flex gap-2">
          <button
            onClick={onClose}
            disabled={submitting}
            className="flex-1 h-12 bg-hover hover:bg-active rounded-xl text-fg-2 font-medium"
          >
            Cancelar
          </button>
          <button
            onClick={() => void handleSubmit()}
            disabled={!cidadeOK || !date || !periodo || submitting}
            className={`flex-1 h-12 ${submitColor} rounded-xl text-white font-bold flex items-center justify-center gap-2`}
          >
            {submitting ? (
              <Loader2 size={18} className="animate-spin" />
            ) : (
              <>
                {squeezeMode ? (
                  <Zap size={16} />
                ) : hasActiveAppointment ? (
                  <RotateCw size={16} />
                ) : (
                  <CalendarIcon size={16} />
                )}
                <span>{submitLabel}</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between py-1.5">
      <span className="text-xs text-fg-2 flex-shrink-0">{label}</span>
      <span className="text-xs text-white font-medium text-right ml-3 truncate">{value}</span>
    </div>
  );
}
